/**
 * Application Config Store
 * Manages runtime configuration fetched from the backend.
 * @packageDocumentation
 */

import { create } from 'zustand';

/**
 * Application configuration from the backend.
 */
export interface AppConfig {
  version: string;
  environment: string;
  auth: {
    iapEnabled: boolean;
  };
}

/**
 * Config store state interface.
 */
interface ConfigState {
  isLoading: boolean;
  hasLoaded: boolean;
  error: string | null;
  config: AppConfig | null;
  fetchConfig: () => Promise<void>;
}

/**
 * Config store for managing runtime configuration.
 * Fetches configuration from /api/config endpoint.
 */
export const useConfigStore = create<ConfigState>((set, get) => ({
  error: null,
  config: null,
  isLoading: false,
  hasLoaded: false,

  fetchConfig: async () => {
    if (get().hasLoaded || get().isLoading) {
      return;
    }

    set({
      error: null,
      isLoading: true,
    });

    try {
      const response = await fetch('/api/config', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }

      const config = (await response.json()) as AppConfig;
      set({
        config,
        hasLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        hasLoaded: true,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        config: {
          version: 'unknown',
          environment: 'unknown',
          auth: {
            iapEnabled: false,
          },
        },
      });
    }
  },
}));
